import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { UserAccessTokenDto } from '../dto/UserDTOs';
import { login, loginByKakao } from '../api/UserAuthApi';
import { ResponseDTO } from '../dto/ResponseDTO';
import { loadConf, saveConf } from '../api/native/NativeApis';
import { getUserInfo, getNamecard } from '../api/UserAuthApi';
import { UserInfoDTO, NamecardDTO } from '../dto/UserDTOs';
import { getSchedulesForTheDay } from '../api/BookcaseApi';
import * as utils from '../common/utils';
import { getUnreadMessageCount } from '../api/NbmessageApi';
import { getBannerForUser } from '../api/BannerManagementApi';
import { BannerInfoDTO } from '../dto/BannerManagementDTOs';
import { BannerType } from '../common/enums';

// 쿼리 키 상수
export const QUERY_KEYS = {
  SCHEDULES: 'schedules',
  SELECTED_DATE: 'selectedDate',
  LOGIN: 'login',
  USER_INFO: 'userInfo',
  NAMECARD: 'namecard',
  UNREAD_MESSAGE_COUNT: 'unreadMessageCount',
  BANNERS: 'banners',
} as const;

// 스케줄 타입 정의
export interface Schedule {
  id: string;
  icon: 'nudok' | 'nupil' | 'moim';
  bookId: number | null;
  title: string;
  period: string;
  dDay: string;
  imgUrl: string | null;
  schType: number | null;
}

/*
// 목업 데이터
const mockSchedules: Schedule[] = [
  {
    id: '1',
    icon: 'nudok',
    title: '2024년 독서 모임',
    period: '2024.01.01 ~ 2024.12.31',
    dDay: 30,
  },
  {
    id: '2',
    icon: 'nupil',
    title: '월간 필사 모임',
    period: '2024.03.01 ~ 2024.03.31',
    dDay: 15,
  },
  {
    id: '3',
    icon: 'moim',
    title: '독서 토론 모임',
    period: '2024.03.15 ~ 2024.03.15',
    dDay: 7,
  },
];*/
/*
class NoobookScheduleDTO {
	
	title:string|null = null;
	bookId:number|null = null;
	startAt:Date|null = null;
	endAt:Date|null = null;
	schType:number|null = null; // 1 : read, 2 : script
}*/
// 스케줄 데이터 가져오기
const fetchSchedules = async (): Promise<Schedule[]> => {
  // 실제 API 호출 대신 목업 데이터 반환
    const {accessToken, refreshToken} = await getAccessToken();
    //console.log("accessToken : " + accessToken);
    const response = await getSchedulesForTheDay(accessToken ?? "", new Date());
    if(response.errorCode === 0){
      //return [];
      return new Promise((resolve)=>{
       resolve(response.response.map(item=>{
       // console.log("item : " + item.startAt?.toDateString());
        return {
          id: item.bookId?.toString() ?? "",
          icon: item.schType === 1 ? 'nudok' : item.schType === 2 ? 'nupil' : 'moim',
          bookId: item.bookId,
          title: item.title ?? "",
          period:utils.stringFrDate(item?.startAt??'', 'yyyy-MM-dd', '-') + ' ~ ' + utils.stringFrDate(item?.endAt??'', 'yyyy-MM-dd', '-'),
          dDay: utils.getDday(item?.endAt),
          imgUrl: item.imgUrl ?? null,
          schType: item.schType ?? null,
        }}))});
    } else {
      //throw new Error(response.errorCode.toString());
      return new Promise((resolve)=>{
        resolve([]);
      });
    }
  }


// 선택된 날짜 가져오기
const getSelectedDate = (): Date => {
  const storedDate = localStorage.getItem(QUERY_KEYS.SELECTED_DATE);
  return storedDate ? new Date(storedDate) : new Date();
};

export const useUserInfoQuery = () => {
  return useQuery<UserInfoDTO>({
    queryKey: [QUERY_KEYS.USER_INFO],
    queryFn: async() => {
      const {accessToken, refreshToken} = await getAccessToken();
      const response = await getUserInfo(accessToken ?? "");
      if(response.errorCode === 0){
        return response.response;
      } else {
        throw new Error(response.errorCode.toString());
      }
    },
    staleTime: 60000,
  });
};

export const useUnreadMessageCountQuery = () => {
  return useQuery<number>({
    queryKey: [QUERY_KEYS.UNREAD_MESSAGE_COUNT],
    queryFn: async() => {
      const {accessToken, refreshToken} = await getAccessToken();
      const response = await getUnreadMessageCount(accessToken ?? "");
      if(response.errorCode === 0){
        return response.response;
      } else {
        throw new Error(response.errorCode.toString());
      }
    },
    staleTime: 20000,
  });
};

export const useBannerListQuery = (bannerType:BannerType) => {
  return useQuery<BannerInfoDTO[]>({
    queryKey: [QUERY_KEYS.BANNERS, bannerType],
    queryFn: async() => {
      const {accessToken, refreshToken} = await getAccessToken();
      const response = await getBannerForUser(accessToken ?? "", bannerType);
      if(response.errorCode === 0){
        return response.response ?? [];
      } else {
        throw new Error(response.errorCode.toString());
      }
    },
    staleTime: 60000,
  });
};

export const useInvalidateUnreadMessageCount = () => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.UNREAD_MESSAGE_COUNT] });
  }, [queryClient]);
};

export const useInvalidateSchedules = () => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SCHEDULES] });
  }, [queryClient]);
};


export const useNamecardQuery = () => {
  return useQuery<NamecardDTO>({
    queryKey: [QUERY_KEYS.NAMECARD],
    queryFn: async() => {
      const {accessToken, refreshToken} = await getAccessToken();
      const response = await getNamecard(accessToken ?? "");
      if(response.errorCode === 0){
        return response.response;
      } else {
        throw new Error(response.errorCode.toString());
      }
    },
    staleTime: 60000,
  });
};

// 커스텀 훅: 스케줄 데이터 조회
export const useSchedulesQuery = () => {
  return useQuery<Schedule[]>({
    queryKey: [QUERY_KEYS.SCHEDULES],
    queryFn: fetchSchedules,
    refetchOnMount: 'always',
  });
};

// 커스텀 훅: 선택된 날짜 조회
export const useSelectedDateQuery = () => {
  return useQuery<Date>({
    queryKey: [QUERY_KEYS.SELECTED_DATE],
    queryFn: getSelectedDate,
    initialData: new Date(),
  });
};
// native webview 상태 조회
export const getNativeWebview = () => {
  const isWebview = localStorage.getItem('isWebview');
  if(isWebview === 'true'){
    return true;
  } else {
    return false;
  } 
}

// native webview 상태 설정
export const setNativeWebview = (isWebview:boolean) => {
  localStorage.setItem('isWebview', isWebview.toString());
}

export const getLastPath = () => {
  return localStorage.getItem('lastPath');
}

export const popLastPath = () => {
  var lastPath = localStorage.getItem('lastPath');
  localStorage.removeItem('lastPath');
  return lastPath;
}

export const setLastPath = (path:string) => {
  localStorage.setItem('lastPath', path);
}

// 로그인
export const clearAccessToken = async() => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('agreementConfirmed');
  localStorage.removeItem('privacyPolicyConfirmed');
  localStorage.removeItem('lastPath');
  const isWebview = getNativeWebview();
  if(isWebview){
    await saveConf({accessToken:"", refreshToken:""});
    //await loadConf();
  }
}

export const getAccessToken = async() => {
  //const navigate = useNavigate();
  //const location = useLocation();
  const isWebview = getNativeWebview();
  if(isWebview){
    const response = await loadConf();
    let ret = {accessToken:"", refreshToken:""};
    if (response.result === 0) {
      //alert('getAccessToken: ' + window.location.href);
      //alert('getAccessToken: ' + JSON.stringify(response));
      ret = {accessToken:response.data.accessToken ?? "", refreshToken:response.data.refreshToken ?? ""};
    } 
    if(!ret.accessToken && !ret.refreshToken){
      if(window.location.pathname !== '/login' && window.location.pathname !== '/'){
        setLastPath(window.location.pathname + window.location.search);
        //alert('setLastPath: ' + window.location.href);
        //window.location.href = '/';

      }
    }
    return ret;
  } else {
    let ret = {accessToken:localStorage.getItem('accessToken'), refreshToken:localStorage.getItem('refreshToken')};
    if(!ret.accessToken && !ret.refreshToken){
      if(window.location.pathname !== '/login' && window.location.pathname !== '/'){
        setLastPath(window.location.pathname + window.location.search);
        //window.location.href = '/';
      }
    }
    return ret;
  }
}

export const getAgreementConfirmed = async() => {
  const isWebview = getNativeWebview();
  if(isWebview){
    const response = await loadConf();
    if(response.result === 0){
      return {agreementConfirmed:response.data.agreementConfirmed, privacyPolicyConfirmed:response.data.privacyPolicyConfirmed};
    } else {
      return {agreementConfirmed:false, privacyPolicyConfirmed:false};
    }
  } else {
    return {agreementConfirmed:localStorage.getItem('agreementConfirmed') === 'true', privacyPolicyConfirmed:localStorage.getItem('privacyPolicyConfirmed') === 'true'};
  } 
}


export const useLoginQuery = (loginId:string, loginPw:string, options?: { enabled: boolean }) => {
  const isWebview = getNativeWebview();
  return useQuery<UserAccessTokenDto>({
    queryKey: [QUERY_KEYS.LOGIN],
    queryFn: async () => {
      try{
        const response = await login(loginId, loginPw);
      if (response.errorCode === 0) {
        if(isWebview){
          //alert('1. useLoginQuery: saveConf: ' + JSON.stringify(response.response));
          //saveConf(response.response);
          await saveConf({accessToken:response.response.accessToken, refreshToken:response.response.refreshToken});
          //saveConf({accessToken:"test1111", refreshToken:response.response.refreshToken});
          const resp = await loadConf();//.then((response)=>{
          if(resp.result === 0){
              //alert('2. useLoginQuery: saveConf: response: ' + JSON.stringify(resp));
              response.response = {...response.response, accessToken:resp.data.accessToken ?? "", refreshToken:resp.data.refreshToken ?? ""};
          }
        } else {
          localStorage.setItem('accessToken', response.response.accessToken);
          localStorage.setItem('refreshToken', response.response.refreshToken);
          localStorage.setItem('agreementConfirmed', response.response.agreementConfirmed.toString());
          localStorage.setItem('privacyPolicyConfirmed', response.response.privacyPolicyConfirmed.toString());
        }
        return response.response;
      }
      throw new Error(response.errorCode.toString());
    } catch(error){
      throw new Error(error instanceof Error ? error.message : String(error));
    }},
    enabled: options?.enabled ?? false
  });
};

export const useKakaoLoginQuery = (authCode:string, options?: { enabled: boolean , staleTime: number }) => {
  const isWebview = getNativeWebview();
  return useQuery<UserAccessTokenDto>({
    queryKey: [QUERY_KEYS.LOGIN],
    queryFn: async () => {
      try{
        const response = await loginByKakao(authCode);
      if (response.errorCode === 0) {
        if(isWebview){
          await saveConf(response.response);
        } else {
          localStorage.setItem('accessToken', response.response.accessToken);
          localStorage.setItem('refreshToken', response.response.refreshToken);
          localStorage.setItem('agreementConfirmed', response.response.agreementConfirmed.toString());
          localStorage.setItem('privacyPolicyConfirmed', response.response.privacyPolicyConfirmed.toString());
        }
        return response.response;
      }
      throw new Error(response.errorCode.toString());
    } catch(error){
      throw new Error(error instanceof Error ? error.message : String(error));
    }},
    enabled: options?.enabled ?? false, staleTime: options?.staleTime ?? 30000,
  });
};
